import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { getProfitAndLossComparison } from '@/api/seller-analytics';
import { ErrorSource, ProfitAndLoss } from '@boxem/core-api-schema';
import { DateRangeValue, endOfToday, startOfToday } from '@/utils/dateUtils';
import { getLocalStorageItem } from '@/utils/localStorageUtils';
import { usePreviousTimeFrame } from '@/hooks/usePreviousTimeFrame';
import { useErrorModalContext } from './ErrorModalContext';

interface ProfitAndLossContextData {
  profitAndLossData: ProfitAndLoss | null;
  isLoading: boolean;
  error: string | null;
  timeFrame: DateRangeValue;
  setTimeFrame: (time: DateRangeValue) => void;
  fetchProfitAndLossData: () => Promise<void>;
  previousTimeFrame: { from: Date; to: Date };
}

const ProfitAndLossContext = createContext<ProfitAndLossContextData | undefined>(undefined);

export const useProfitAndLoss = () => {
  const context = useContext(ProfitAndLossContext);
  if (context === undefined) {
    throw new Error('useProfitAndLoss must be used within a ProfitAndLossAnalyticsProvider');
  }
  return context;
};

export const ProfitAndLossAnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { setErrors } = useErrorModalContext();
  const [profitAndLossData, setProfitAndLossData] = useState<ProfitAndLoss | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [timeFrame, setTimeFrame] = useState<DateRangeValue>(
    getLocalStorageItem('profitLossTimeFrame', {
      from: startOfToday.startOf('month').startOf('day').toDate(),
      to: endOfToday.toDate(),
      label: 'This month'
    })
  );


  const previousTimeFrame = usePreviousTimeFrame(timeFrame);

  const fetchProfitAndLossData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getProfitAndLossComparison({
        firstInterval: previousTimeFrame,
        secondInterval: {
          from: timeFrame.from,
          to: timeFrame.to
        }
      });
      setProfitAndLossData(response);
    } catch (err) {
      console.error('Error fetching profit and loss data:', err);
      setError('Failed to fetch profit and loss data. Please try again.');
      setErrors([{ source: ErrorSource.BOXEM, message: 'Unable to load profitability data. Please try again' }]);
    } finally {
      setIsLoading(false);
    }
  }, [timeFrame, previousTimeFrame]);

  return (
    <ProfitAndLossContext.Provider
      value={{
        profitAndLossData,
        isLoading,
        error,
        timeFrame,
        setTimeFrame,
        fetchProfitAndLossData,
        previousTimeFrame,
      }}
    >
      {children}
    </ProfitAndLossContext.Provider>
  );
};